import { IconDownload } from "@tabler/icons-react"
import { type FC, useRef, useState } from "react"
import { Navigate, useParams } from "react-router"

import { parseNumber } from "@forento/core/utilities/number"
import { toSlug } from "@forento/core/utilities/string"
import Certificate, { CertificateLine, CertificateValue } from "@forento/ui/components/Certificate"

import Button from "~/components/Button"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { usePlatform } from "~/contexts/UserContext"
import { downloadElement } from "~/utilities/element"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const UserCertificatePage: FC = () => {
	const userId = parseNumber(useParams().userId) ?? -1
	const certificateId = parseNumber(useParams().certificateId) ?? -1
	const platform = usePlatform()!

	const { data: certificate, error } = query.course.certificate.getForCreator.useQuery({
		userId,
		certificateId,
	})

	const certificateRef = useRef<HTMLDivElement>(null)
	const [isDownloading, setDownloading] = useState(false)

	if (certificate === null) {
		return <Navigate to={userId > 0 ? routes.user.detail(userId) : routes.user.index()} replace />
	}

	const handleDownload = async () => {
		if (!certificate || !certificateRef.current) return

		setDownloading(true)
		try {
			await downloadElement(
				certificateRef.current,
				`${toSlug(`${certificate.user.firstName} ${certificate.user.lastName} ${certificate.course.title}`)}-certificate.png`,
			)
		} catch (error) {
			console.error(error)
			setDownloading(false)
			return Promise.reject(error)
		}
		setDownloading(false)
	}

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					path={[
						{ title: "Users", link: routes.user.index() },
						{
							title: certificate ? `${certificate.user.firstName} ${certificate.user.lastName}` : "...",
							link: userId > 0 ? routes.user.detail(userId) : undefined,
						},
					]}
					title={certificate?.course.title ?? "Certificate"}
				/>
				{certificate && (
					<Button variant="primary" onClick={handleDownload} isLoading={isDownloading}>
						<IconDownload className="size-4" />
						Download
					</Button>
				)}
			</PageHeader>
			{error ? (
				<p className="text-danger font-medium">Failed to load certificate.</p>
			) : !certificate ? (
				<PartialLoadingPage />
			) : (
				<div className="overflow-x-auto pb-2">
					<Certificate
						accentColor={platform.primaryColor.value}
						elementRef={certificateRef}
						issuerName={platform.name}
						logo={platform.logo}
						title="Certificate of completion"
						className="w-225"
					>
						<CertificateLine>This is to certify that</CertificateLine>
						<CertificateValue>
							{certificate.user.firstName} {certificate.user.lastName}
						</CertificateValue>
						<CertificateLine>has successfully completed the course</CertificateLine>
						<CertificateValue>{certificate.course.title}</CertificateValue>
						<CertificateLine>
							on{" "}
							{new Intl.DateTimeFormat(platform.language, { dateStyle: "long" }).format(
								certificate.createDate,
							)}
						</CertificateLine>
					</Certificate>
				</div>
			)}
		</Layout>
	)
}

export default UserCertificatePage
